import { Component, EventEmitter, Input, OnChanges, OnInit, Output, ViewChild, DoCheck } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { HotToastService } from '@ngneat/hot-toast';
import * as dayjs from 'dayjs';

import { PersonalLookupInfoForTestRegistrationDto, TestSessionForRegistrationDto, UserAccountIdentityInfoDto } from 'src/app/core/dtos/dto';
import { BrandEnum, CountryEnum, IdDocTypeEnum } from 'src/app/core/dtos/enum';
import { ProofOfIdentityPageSaveInfo } from 'src/app/core/dtos/reg-data';
import { ConfigHelper } from 'src/app/core/services/helpers/config.helper';
import { ModalComponent } from 'src/app/shared/components/modal/modal.component';

@Component({
  selector: 'app-proof-of-identity-form',
  templateUrl: './proof-of-identity-form.component.html',
  styleUrls: ['./proof-of-identity-form.component.scss']
})
export class ProofOfIdentityFormComponent implements OnInit, OnChanges {

  @Input() proofOfIdentityPageSaveInfo: ProofOfIdentityPageSaveInfo;
  @Input() personalLookupInfo: PersonalLookupInfoForTestRegistrationDto;
  @Input() testSession: TestSessionForRegistrationDto;

  @Input() identityInfo: UserAccountIdentityInfoDto;
  @Input() testDate: Date;
  @Output() formSubmit = new EventEmitter<ProofOfIdentityPageSaveInfo>();
  @ViewChild('expiredRefugeeDocumentModal') expiredRefugeeDocumentModal: ModalComponent;

  registrationForm: FormGroup;
  get f() { return this.registrationForm.controls; }

  brandId: BrandEnum;
  submitted = false;
  showIdDocExpiryError: boolean;
  expiryMinDate = new Date();
  showBNOPassportWarning = false;
  sendEmailLink: string;
  brandName: string;
  isDocValidforIrcc: boolean;
  validIrccDocs = [IdDocTypeEnum.Passport, IdDocTypeEnum.UnitedStatesPassportCard, IdDocTypeEnum.CanadianPermanentResidentCard, IdDocTypeEnum.CanadianRefugeeProtectionClaimantDocument, IdDocTypeEnum.RefugeeTravelDocument, IdDocTypeEnum.ComputerizedNationalIdentityCard];

  constructor(
    private formBuilder: FormBuilder,
    private toastService: HotToastService,
    private configHelper: ConfigHelper
  ) { }

  ngOnInit() {
    this.brandId = this.configHelper.getBrandId();
    this.brandName = this.configHelper.getBrandName();
    this.createForm();
  }

  ngOnChanges() {
    this.createForm();
  }

  ngDoCheck() {
    if (this.isDocValidforIrcc == undefined) {
      this.checkValidIrccDoc();
    }
  }

  public get IdDocTypeEnum() {
    return IdDocTypeEnum;
  }

  public get BrandEnum() {
    return BrandEnum;
  }

  createForm() {
    if (!this.registrationForm) {
      this.registrationForm = this.formBuilder.group({
        countryId: [this.identityInfo?.citizenshipCountryId, Validators.required],
        firstLanguageId: ['', Validators.required],
        firstName: [{ value: this.identityInfo?.firstName, disabled: true }],
        middleName: [{ value: this.identityInfo?.middleName, disabled: true }],
        lastName: [{ value: this.identityInfo?.lastName, disabled: true }],
        gender: [{ value: this.identityInfo?.gender, disabled: true }],
        documentExpiryDate: [{ value: dayjs(this.identityInfo?.birthDate).format('YYYY-MM-DD'), Validators: true }],
        dateOfBirth: [{ value: dayjs(this.identityInfo?.birthDate).format('YYYY-MM-DD'), disabled: true }],
        documentTypeId: [this.identityInfo?.idDocTypeId, this.documentTypeIdValid(this.personalLookupInfo)],
        documentNumber: [this.identityInfo?.idDocNumber, Validators.required],
        documentConfirm: ['', Validators.requiredTrue],
        skipLastName: [false]
      });
      this.registrationForm.patchValue(this.identityInfo);
      this.registrationForm.controls?.documentExpiryDate.setValue(dayjs(this.identityInfo?.idDocExpiryDate).format('YYYY-MM-DD'));
      this.setFormValidator();
    }
  }


  checkExpiryDateValidity() {
    if (this.f.documentExpiryDate) {
      const testDate = dayjs(this.testDate).startOf('day');
      const day = dayjs(this.f.documentExpiryDate.value);
      if (day < testDate) {
        this.showIdDocExpiryError = true;
      } else {
        this.showIdDocExpiryError = false;
      }
      if (Number(this.f.documentTypeId.value) === IdDocTypeEnum.CanadianRefugeeProtectionClaimantDocument) {
        this.showIdDocExpiryError = false;
      }
    }
  }

  checkRefugeeClaimant() {
    if (this.brandId === BrandEnum.CAEL) {
      this.sendEmailLink = '<a href="mailto:info@cael.ca" target="_top">info@cael.ca</a>';
    } else {
      this.sendEmailLink = '<a href="mailto:info@celpip.ca" target="_top">info@celpip.ca</a>';
    }
    if (this.f.documentExpiryDate && !this.identityInfo.refugeeDocumentApproved) {
      const testDate = dayjs(this.testDate).startOf('day');
      const day = dayjs(this.f.documentExpiryDate.value);
      if (day < testDate && (Number(this.f.documentTypeId.value) === IdDocTypeEnum.CanadianRefugeeProtectionClaimantDocument)) {
        this.expiredRefugeeDocumentModal.open();
      }
    }
  }

  documentTypeIdValid = (personalLookup: PersonalLookupInfoForTestRegistrationDto) => (control: FormControl) => {
    const selection: number = control.value * 1;
    if (personalLookup) {
      for (const idDocType of personalLookup.idDocTypes) {
        if (idDocType.id === selection) {
          return null;
        }
      }
    }
    return { requireMatch: true };
  };

  setFormValidator() {
    const lastNameControl = this.registrationForm.get('lastName');
    this.registrationForm.get('skipLastName').valueChanges
      .subscribe(skipLastName => {
        if (skipLastName === true) {
          lastNameControl.setValidators(null);
        } else {
          lastNameControl.setValidators(Validators.required);
        }
        lastNameControl.updateValueAndValidity();
      });
  }

  onSubmit() {
    this.submitted = true;
    const date = new Date(this.f.documentExpiryDate.value).getFullYear();
    if (!date && this.f.documentExpiryDate) {
      this.toastService.warning("Please enter a valid expiry date.");
      return;
    }
    if (this.showIdDocExpiryError) {
      this.toastService.warning("The ID document must not have expired before the test date.");
      return;
    }
    if (this.registrationForm.valid) {
      //use getRawValue instead of get value to include all form values regardless of disabled status
      this.formSubmit.emit(this.registrationForm.getRawValue());
    } else {
      this.toastService.warning("Please fill in the missing fields");
    }
  }

  checkShowBNOWarning() {
    const isCelpip = this.configHelper.getBrandId() === BrandEnum.CELPIP;
    const registrationInChina = this.testSession.testCentreCountryId === CountryEnum.China;
    const usingPassport = Number(this.f.documentTypeId.value) === IdDocTypeEnum.Passport;
    this.showBNOPassportWarning = isCelpip && registrationInChina && usingPassport;
  }

  updateExpiryMinDate() {
    if (Number(this.f.documentTypeId.value) === IdDocTypeEnum.CanadianRefugeeProtectionClaimantDocument) {
      this.expiryMinDate = new Date("1900-01-01");
    } else {
      this.expiryMinDate = new Date();
    }
  }

  checkValidIrccDoc() {
    if (this.brandId === BrandEnum.CELPIP && this.validIrccDocs.includes(Number(this.f.documentTypeId.value))) {
      this.isDocValidforIrcc = true;
    }
    else {
      this.isDocValidforIrcc = false;
    }
  }

  idDocTypeUpdated() {
    this.checkShowBNOWarning();
    this.updateExpiryMinDate();
    this.checkValidIrccDoc();
  }

}
